import type { AssignmentScope } from '@Application/Ai/AiConfiguration/Assignments/AssignmentScope';
import ClearModelAssignment from '@Application/Ai/AiConfiguration/Assignments/Commands/ClearModelAssignment';
import clearModelAssignment from '@Application/Ai/AiConfiguration/Assignments/clearModelAssignment';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import type ModelAssignmentView from '@UI/Store/AiConfiguration/Views/ModelAssignmentView';
import { useDispatch } from '@verclam/synapse';
import { Button, ConfirmDialog } from '@verclam/ui-kit';
import { RotateCcw } from 'lucide-react';
import * as React from 'react';

/**
 * Names the level one reverts to from `scope.type`, not from `assignment.effectiveLevel`: this
 * button only renders while an override exists at `scope` (see the guard below), and in that state
 * the server resolves `effectiveLevel` to *this* scope's own level — the own assignment being itself
 * the effective one — never to the level above it. The level above is fixed by the hierarchy
 * (facility → organization → account) and is read off `scope.type` instead.
 */
const PARENT_LEVEL_PHRASE: Record<'organization' | 'facility', string> = {
	organization: 'celui du compte',
	facility: 'celui de la structure',
};

type Props = {
	readonly assignment: ModelAssignmentView;
	readonly scope: AssignmentScope;
};

/**
 * Leaf action, icon button in the card's header row: dispatches itself, never a callback prop.
 * Renders nothing at the account level (the top of the hierarchy — "revert to inherited" has nowhere
 * to go, R3), nothing for a live usage (its switch owns the way back) and nothing while no override
 * exists at `scope` to remove. Its confirmation is reversible (`ConfirmDialog`, `accept` —
 * `ConfirmDeleteDialog`'s `acceptCallback` does not apply here) and its visibility is local
 * `useState`: the context (this usage, this scope) never needs to be read by another component.
 */
const UsageAssignmentCardRevertAction: React.FC<Props> = ({ assignment, scope }) => {
	const dispatch = useDispatch();
	const [isRevertConfirmVisible, setRevertConfirmVisible] = React.useState<boolean>(false);

	const handleRevertClick = React.useCallback(() => {
		setRevertConfirmVisible(true);
	}, []);

	const handleRevertHide = React.useCallback(() => {
		setRevertConfirmVisible(false);
	}, []);

	const handleRevertAccept = React.useCallback(() => {
		dispatch(clearModelAssignment(new ClearModelAssignment(scope, assignment.usage.value))).catch(
			ignoreHandledError,
		);
		setRevertConfirmVisible(false);
	}, [dispatch, scope, assignment.usage.value]);

	if (scope.type === 'account' || assignment.ownModelId === null || assignment.isLive) {
		return null;
	}

	return (
		<React.Fragment>
			<Button
				type="button"
				rounded
				text
				severity="secondary"
				icon={<RotateCcw size={16} />}
				aria-label={`Revenir au modèle hérité pour ${assignment.usage.label}`}
				tooltip={`Retirer le modèle défini ici et reprendre ${PARENT_LEVEL_PHRASE[scope.type]}.`}
				tooltipOptions={{ position: 'top' }}
				onClick={handleRevertClick}
				css={{
					width: '3.5rem',
					height: '3rem',
					flex: '1 1 auto',
				}}
			/>
			<ConfirmDialog
				blockScroll
				closable
				draggable={false}
				visible={isRevertConfirmVisible}
				onHide={handleRevertHide}
				header="Revenir à l'hérité"
				message={
					<div>
						<p>
							Le modèle défini pour {assignment.usage.label.toLowerCase()} à ce niveau sera
							retiré.
						</p>
						<p>L'usage redeviendra hérité du niveau au-dessus.</p>
						<p>Cette action est réversible : vous pourrez redéfinir un modèle à tout moment.</p>
					</div>
				}
				accept={handleRevertAccept}
				reject={handleRevertHide}
			/>
		</React.Fragment>
	);
};

export default UsageAssignmentCardRevertAction;
